import React, { useEffect, useMemo, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import { StyledInput } from "../../../components/inputs/styledInput";
import { createTemplateSchema } from "../validation/createTemplate";
import {
  useSubPageTemplateListQuery,
  useUpdateTemplateSubPageMutation,
} from "../../../graphql/subpage.hooks";
import { Error } from "../../../utils";

interface IEditSubPageTemplate {
  name: string;
  pageId: number | null;
  position: number | null;
}

export const EditForm = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();

  const [errorsData, setErrors] = useState<{
    name: string;
    pageId: string;
    position: string;
  }>({ name: "", pageId: "", position: "" });

  const [updateSubpageTemplate, { loading: updatingLoading }] =
    useUpdateTemplateSubPageMutation();
  const { data: subpageTemplates, refetch: subpageTemplatesRefresh } =
    useSubPageTemplateListQuery();

  const templateData = useMemo(
    () =>
      subpageTemplates?.subPageTemplateList?.find(
        (template) => template.id === +templateId
      ),
    [templateId, subpageTemplates]
  );

  useEffect(() => {
    document.title = "Edit Subpage Template";
  }, []);

  const initialValues: IEditSubPageTemplate = {
    name: templateData?.name || "",
    pageId: templateData?.page?.id || null,
    position: templateData?.position || null,
  };

  const tryUpdateSubpageTemplate = async (values: IEditSubPageTemplate) => {
    try {
      const { data } = await updateSubpageTemplate({
        variables: {
          ...values,
          id: +templateId,
          position: values.position || null,
        },
      });
      if (data.updateTemplateSubPage.id) {
        subpageTemplatesRefresh();
        backToList();
      }
    } catch (error) {
      console.error(error);
      if (error.message === Error.NOT_FOUND_PAGE) {
        setErrors({ ...errorsData, pageId: Error.NOT_FOUND_PAGE });
      }
    }
  };

  const backToList = () => {
    navigate("/subpage-templates");
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="mb-4">
          <h1>Edit Subpage Template</h1>
        </div>
      </div>
      <div className="g-4 py-5 d-flex flex-column justify-content-center align-items-center bg-white rounded">
        <div className="d-flex flex-column flex-md-row justify-content-between col-11 col-md-8">
          <Formik
            initialValues={initialValues}
            validationSchema={createTemplateSchema}
            onSubmit={tryUpdateSubpageTemplate}
            enableReinitialize
          >
            {({ handleSubmit, touched, errors }) => (
              <Form className="w-100">
                <div className="form-floating mb-3 w-100">
                  <StyledInput
                    name="name"
                    label="Name"
                    error={!!(touched.name && errors.name)}
                  />
                </div>
                <div className="form-floating mb-3 w-100">
                  <StyledInput
                    name="pageId"
                    label="Page Id"
                    type="number"
                    min={1}
                    className={`numberForm ${errorsData.pageId && "is-invalid"}`}
                    error={!!(touched.pageId && errors.pageId)}
                  />
                  {errorsData.pageId && (
                    <p className="mt-1 fs-7 text-danger">{errorsData.pageId}</p>
                  )}
                </div>
                <div className="form-floating mb-3 w-100">
                  <StyledInput
                    name="position"
                    label="Position (optional)"
                    type="number"
                    className="numberForm"
                    min={0}
                    error={!!(touched.position && errors.position)}
                  />
                </div>
                <div className="d-flex flex-column flex-md-row justify-content-between">
                  <button
                    onClick={backToList}
                    className="btn btn-primary text-white col-12 col-md-4 mb-2 mb-md-0"
                  >
                    Back To List
                  </button>
                  <button
                    type="button"
                    disabled={updatingLoading}
                    onClick={() => handleSubmit()}
                    className="btn btn-primary text-white col-12 col-md-4 py-3 mb-4 mb-md-0"
                  >
                    Update
                    {updatingLoading && (
                      <Spinner
                        animation="border"
                        role="status"
                        className="ms-2"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};
